import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { tz } from '@date-fns/tz';
import { NonexistentWallClockError, wallClockToUtcIso } from '@wit-support/ui';
/**
 * Timezone context. Default is UTC; admin-web supplies the logged-in admin's
 * preferred timezone at the root of the tree.
 */
const Ctx = React.createContext('UTC');
export function TimezoneProvider({ tz: zone, children, }) {
    return _jsx(Ctx.Provider, { value: zone, children: children });
}
export function useAdminTimezone() {
    return React.useContext(Ctx);
}
/**
 * Detect the browser's IANA timezone. Falls back to UTC when Intl isn't
 * available or reports no zone. Used as a default when the admin hasn't
 * persisted a preference.
 */
export function detectBrowserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
    catch {
        return 'UTC';
    }
}
/**
 * Format an ISO string or Date in a given zone using a date-fns pattern.
 *
 * @date-fns/tz v1 exposes `tz(zoneId)` — passed via the `in` option,
 * date-fns re-interprets the date's wall-clock in that zone.
 *
 * Локаль — всегда русская: весь интерфейс админки на русском (i18n-слоя нет),
 * а без `locale` date-fns пишет месяцы и дни недели по-английски («4 Oct
 * 2026» посреди русского экрана). Числовые шаблоны (`dd.MM`, `yyyy-MM-dd`) от
 * локали не зависят, так что машинные форматы не меняются.
 */
export function formatInZone(value, pattern, zone) {
    if (value == null || value === '')
        return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime()))
        return '';
    return format(d, pattern, { in: tz(zone), locale: ru });
}
// Wall-clock ⇄ UTC (DST-correct) lives in `@wit-support/ui` (`lib/zoned-time.ts`)
// so `ui-forms` (`DateTimeWidget`) uses the same conversion; re-exported here
// for existing callers.
export { NonexistentWallClockError, wallClockToUtcIso } from '@wit-support/ui';
/**
 * Start-of-day (00:00) in `zone` for the given wall-clock date (YYYY-MM-DD),
 * as a UTC ISO instant. Thin wrapper over `wallClockToUtcIso` at hh:mm 00:00.
 * Returns `undefined` for a malformed date string, or when 00:00 falls inside
 * a DST spring-forward gap in `zone` (rare, but see `wallClockToUtcIso`).
 */
export function startOfDayUtc(ymd, zone) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m)
        return undefined;
    const [, ys, mo, d] = m;
    try {
        return wallClockToUtcIso(Number(ys), Number(mo), Number(d), 0, 0, zone);
    }
    catch (err) {
        if (err instanceof NonexistentWallClockError)
            return undefined;
        throw err;
    }
}
/**
 * Shift a calendar date (YYYY-MM-DD) by whole days, zone-agnostic: the
 * arithmetic runs on a UTC-midnight `Date` so no DST transition can swallow or
 * double a day. A malformed string comes back unchanged. The companion of
 * `startOfDayUtc` for an EXCLUSIVE upper bound — `startOfDayUtc(addDaysYmd(to,
 * 1), zone)` is «the first instant after the last selected day».
 */
export function addDaysYmd(ymd, days) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m)
        return ymd;
    const [, ys, mo, d] = m;
    const date = new Date(Date.UTC(Number(ys), Number(mo) - 1, Number(d)));
    date.setUTCDate(date.getUTCDate() + days);
    const yy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
}
/**
 * Start-of-day (00:00) in `zone` for "today", as a browser-local epoch-ms —
 * used e.g. as a Calendar min-date guard so it disables days before today IN
 * THE ADMIN TZ rather than the browser zone.
 */
export function startOfTodayInZone(zone) {
    const now = new Date();
    const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [ys, mo, d] = dtf.format(now).split('-').map(Number);
    // Local-midnight of the admin-tz "today" — callers typically select/compare
    // Dates in the browser's local zone, so the guard must be a browser-local
    // midnight.
    return new Date(ys, mo - 1, d).getTime();
}
//# sourceMappingURL=time.js.map